import React, { useState } from 'react';
import { User, Mail, Lock, Building, Info, Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/Button';
import { FormField } from '../ui/FormField';
import { Alert } from '../ui/Alert';

interface SignUpFormProps {
  onSubmit: (email: string, password: string, userData: { full_name: string; role: 'admin' | 'client' }) => Promise<void>;
  loading: boolean;
  error: string;
  success: string;
  onSwitchToSignIn: () => void;
}

export const SignUpForm: React.FC<SignUpFormProps> = ({
  onSubmit,
  loading,
  error,
  success,
  onSwitchToSignIn,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<'admin' | 'client'>('client');
  const [showPassword, setShowPassword] = useState(false);
  const [showDemoInfo, setShowDemoInfo] = useState(false);
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});

  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    
    if (!fullName.trim()) {
      errors.fullName = 'Full name is required';
    }
    
    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters long';
    }
    
    if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    console.log('SignUpForm: Submitting with role:', role);
    await onSubmit(email, password, { full_name: fullName, role });
  };

  const fillDemoData = (demoRole: 'admin' | 'client') => {
    if (demoRole === 'admin') {
      setEmail('admin@aicrm.com');
      setFullName('Admin User');
      setRole('admin');
    } else {
      setEmail('client@example.com');
      setFullName('Client User');
      setRole('client');
    }
    setPassword('demo123456');
    setConfirmPassword('demo123456');
    setShowDemoInfo(true);
    setFormErrors({});
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-6">
      {error && <Alert type="error" message={error} />}
      {success && <Alert type="success" message={success} />}
      
      {showDemoInfo && (
        <Alert 
          type="info" 
          message="Demo credentials filled! If this email is already registered, switch to Sign In and use password: demo123456"
        />
      )}

      <FormField
        label="Full Name"
        icon={User}
        required
        helpText={formErrors.fullName}
      >
        <input
          type="text"
          value={fullName}
          onChange={(e) => {
            setFullName(e.target.value);
            if (formErrors.fullName) {
              setFormErrors({...formErrors, fullName: ''});
            }
          }}
          className={`w-full pl-10 pr-4 py-3 border ${formErrors.fullName ? 'border-error-500 bg-error-50' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200`}
          placeholder="Enter your full name"
          required
        />
      </FormField>

      <FormField
        label="Email Address"
        icon={Mail}
        required
        helpText={formErrors.email}
      >
        <input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (formErrors.email) {
              setFormErrors({...formErrors, email: ''});
            }
          }}
          className={`w-full pl-10 pr-4 py-3 border ${formErrors.email ? 'border-error-500 bg-error-50' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200`}
          placeholder="Enter your email"
          required
        />
      </FormField>

      <FormField
        label="Password"
        icon={Lock}
        required
        helpText={formErrors.password || "Password must be at least 6 characters long"}
      >
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (formErrors.password) {
                setFormErrors({...formErrors, password: ''});
              }
            }}
            className={`w-full pl-10 pr-12 py-3 border ${formErrors.password ? 'border-error-500 bg-error-50' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200`}
            placeholder="Create a password"
            required
            minLength={6}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
      </FormField>

      <FormField
        label="Confirm Password"
        icon={Lock}
        required
        helpText={formErrors.confirmPassword}
      >
        <input
          type={showPassword ? "text" : "password"}
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            if (formErrors.confirmPassword) {
              setFormErrors({...formErrors, confirmPassword: ''});
            }
          }}
          className={`w-full pl-10 pr-4 py-3 border ${formErrors.confirmPassword ? 'border-error-500 bg-error-50' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200`}
          placeholder="Confirm your password"
          required
        />
      </FormField>

      <FormField
        label="Account Type"
        icon={Building}
        required
        helpText="Choose Admin if you are the business owner, Client if you are a customer"
      >
        <div className="grid grid-cols-2 gap-4 mt-2">
          <div
            className={`border rounded-lg p-4 flex flex-col items-center cursor-pointer transition-all ${
              role === 'admin'
                ? 'border-primary-500 bg-primary-50 shadow-sm'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
            onClick={() => setRole('admin')}
          >
            <Building className={`w-8 h-8 mb-2 ${role === 'admin' ? 'text-primary-600' : 'text-gray-400'}`} />
            <span className={`font-medium ${role === 'admin' ? 'text-primary-700' : 'text-gray-700'}`}>Admin</span>
            <span className="text-xs text-gray-500 mt-1 text-center">Business owner/manager</span>
          </div>
          
          <div
            className={`border rounded-lg p-4 flex flex-col items-center cursor-pointer transition-all ${
              role === 'client'
                ? 'border-primary-500 bg-primary-50 shadow-sm'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
            onClick={() => setRole('client')}
          >
            <User className={`w-8 h-8 mb-2 ${role === 'client' ? 'text-primary-600' : 'text-gray-400'}`} />
            <span className={`font-medium ${role === 'client' ? 'text-primary-700' : 'text-gray-700'}`}>Client</span>
            <span className="text-xs text-gray-500 mt-1 text-center">Customer/client</span>
          </div>
        </div>
      </FormField>

      <Button
        type="submit"
        size="lg"
        loading={loading}
        className="w-full bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 transform hover:scale-[1.01] transition-all duration-200"
        disabled={loading}
      >
        Create Account
      </Button>

      <div className="text-center">
        <button
          type="button"
          onClick={onSwitchToSignIn}
          className="text-primary-600 hover:text-primary-700 text-sm font-medium transition-colors duration-200"
        >
          Already have an account? Sign in
        </button>
      </div>

      <div className="border-t pt-4">
        <div className="flex items-center gap-2 mb-3">
          <Info className="w-4 h-4 text-blue-500" />
          <p className="text-sm text-gray-600">Quick demo setup:</p>
        </div>
        <div className="flex space-x-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => fillDemoData('admin')}
            className="flex-1"
            type="button"
          >
            Admin Demo
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => fillDemoData('client')}
            className="flex-1"
            type="button"
          >
            Client Demo
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-2 text-center">
          Demo password: demo123456
        </p>
      </div>
    </form>
  );
};